import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/account/change-password')({ component: ChangePasswordPage });

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(8, 'Must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });
type Values = z.infer<typeof schema>;

function ChangePasswordPage() {
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const submit = useMutation({
    mutationFn: (v: Values) =>
      api('/account/change-password', {
        method: 'POST',
        json: { currentPassword: v.currentPassword, newPassword: v.newPassword },
      }),
    onSuccess: () => form.reset(),
    onError: (err) => {
      if (err instanceof ApiError) {
        const p = err.problem as { title?: string; errors?: Record<string, string[]> } | null;
        setError(p?.title ?? Object.values(p?.errors ?? {}).flat()[0] ?? 'Could not change password');
      } else setError('Could not reach server');
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="section-ref">Password</div>
        <h2 className="mt-1.5 font-serif text-2xl font-medium tracking-tight">Change your password</h2>
        <div className="mt-1 gov-rule" aria-hidden />
        <p className="mt-4 max-w-prose text-[14px] leading-relaxed text-(--color-ink-2)">
          Choose a long, unique passphrase. We recommend at least four random words you can
          remember. Avoid passwords used on other sites.
        </p>
      </div>

      <Card>
        <CardContent className="p-7">
          <form
            onSubmit={form.handleSubmit((v) => {
              setError(null);
              submit.mutate(v);
            })}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="cp-current" required>
                Current password
              </Label>
              <Input
                id="cp-current"
                type="password"
                autoComplete="current-password"
                aria-invalid={!!form.formState.errors.currentPassword || undefined}
                {...form.register('currentPassword')}
              />
              {form.formState.errors.currentPassword && (
                <FieldError msg={form.formState.errors.currentPassword.message} />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-new" required>
                New password
              </Label>
              <Input
                id="cp-new"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!form.formState.errors.newPassword || undefined}
                {...form.register('newPassword')}
              />
              {form.formState.errors.newPassword && (
                <FieldError msg={form.formState.errors.newPassword.message} />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-confirm" required>
                Confirm new password
              </Label>
              <Input
                id="cp-confirm"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!form.formState.errors.confirmPassword || undefined}
                {...form.register('confirmPassword')}
              />
              {form.formState.errors.confirmPassword && (
                <FieldError msg={form.formState.errors.confirmPassword.message} />
              )}
            </div>

            {error && (
              <p className="flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2.5 text-[13px] font-medium text-(--color-maroon)">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                {error}
              </p>
            )}
            {submit.isSuccess && (
              <p className="flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] border-(--color-forest) bg-(--color-forest-soft) px-3 py-2.5 text-[13px] font-medium text-(--color-forest)">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                Password changed.
              </p>
            )}
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submit.isPending}>
                <Lock className="size-4" />
                {submit.isPending ? 'Saving…' : 'Save password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FieldError({ msg }: { msg?: string }) {
  return (
    <p className="flex items-center gap-1.5 text-[12px] font-medium text-(--color-maroon)">
      <AlertTriangle className="size-3.5" />
      {msg}
    </p>
  );
}
